import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Home,
  MapPin,
  FileText,
  Settings,
  Wifi,
  Camera,
  Cloud,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Check,
  X,
  Loader2,
  Plus,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  Zap,
  Thermometer,
  Phone,
  Mail,
  Users,
  Search,
  Globe,
  LayoutGrid,
  Wrench,
  Calendar,
  Ticket,
  Car,
  Bot,
  Package,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "./i18n/LanguageContext";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categorie catastali
const CATEGORIE_CATASTALI = [
  { value: "A/1", label: "A/1 - Abitazioni signorili" },
  { value: "A/2", label: "A/2 - Abitazioni civili" },
  { value: "A/3", label: "A/3 - Abitazioni economiche" },
  { value: "A/4", label: "A/4 - Abitazioni popolari" },
  { value: "A/5", label: "A/5 - Abitazioni ultrapopolari" },
  { value: "A/6", label: "A/6 - Abitazioni rurali" },
  { value: "A/7", label: "A/7 - Villini" },
  { value: "A/8", label: "A/8 - Ville" },
  { value: "A/9", label: "A/9 - Castelli/palazzi storici" },
  { value: "A/10", label: "A/10 - Uffici/studi privati" },
  { value: "A/11", label: "A/11 - Abitazioni tipiche" },
];

const CLASSI_ENERGETICHE = [
  { value: "A4", label: "A4", color: "bg-green-600" },
  { value: "A3", label: "A3", color: "bg-green-500" },
  { value: "A2", label: "A2", color: "bg-green-400" },
  { value: "A1", label: "A1", color: "bg-lime-500" },
  { value: "B", label: "B", color: "bg-lime-400" },
  { value: "C", label: "C", color: "bg-yellow-400" },
  { value: "D", label: "D", color: "bg-orange-400" },
  { value: "E", label: "E", color: "bg-orange-500" },
  { value: "F", label: "F", color: "bg-red-400" },
  { value: "G", label: "G", color: "bg-red-600" },
];

// Password Input con toggle visibilità
const PasswordInput = ({ value, onChange, placeholder, id }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <div className="relative">
      <Input
        id={id}
        type={showPassword ? "text" : "password"}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

// Status Badge per integrazioni
const IntegrationStatus = ({ enabled, connected, name }) => {
  if (!enabled) {
    return <Badge variant="secondary" className="text-xs">Disabilitato</Badge>;
  }
  return connected ? (
    <Badge className="bg-green-500 text-xs">
      <Check className="h-3 w-3 mr-1" /> Connesso
    </Badge>
  ) : (
    <Badge variant="destructive" className="text-xs">
      <X className="h-3 w-3 mr-1" /> Non configurato
    </Badge>
  );
};

// eWeLink Status Component
const EweLinkStatus = ({ authToken }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkStatus();
  }, [authToken]);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ewelink/status`, { params: { token: authToken } });
      setStatus(response.data);
    } catch (error) {
      setStatus({ connected: false, message: "Errore connessione" });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Badge variant="secondary" className="text-xs"><Loader2 className="h-3 w-3 animate-spin" /></Badge>;
  }

  if (status?.connected) {
    return (
      <Badge className="bg-green-500 text-xs">
        <Check className="h-3 w-3 mr-1" /> {status.user?.email || "Connesso"}
      </Badge>
    );
  }

  return (
    <Badge variant="destructive" className="text-xs">
      <X className="h-3 w-3 mr-1" /> Non connesso
    </Badge>
  );
};

// Balin GPS Status Component
const BalinStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/balin/status`);
      setStatus(response.data);
    } catch (error) {
      setStatus({ connected: false, message: "Errore connessione" });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Badge variant="secondary" className="text-xs"><Loader2 className="h-3 w-3 animate-spin" /></Badge>;
  }

  if (status?.connected) {
    return (
      <Badge className="bg-emerald-500 text-xs">
        <Check className="h-3 w-3 mr-1" /> {status.device_count} veicoli
      </Badge>
    );
  }

  return (
    <Badge variant="secondary" className="text-xs">
      <X className="h-3 w-3 mr-1" /> {status?.message || "Non configurato"}
    </Badge>
  );
};

// eWeLink Login Section Component
const EweLinkLoginSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUrl, setAuthUrl] = useState(null);

  useEffect(() => {
    checkStatus();
    
    // Check for OAuth callback result
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('ewelink_auth') === 'success') {
      toast.success(`eWeLink connesso! Email: ${urlParams.get('email') || ''}`);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      checkStatus();
    }
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ewelink/status`);
      setStatus(response.data);
    } catch (error) {
      setStatus({ connected: false, auth_required: true });
    } finally {
      setLoading(false);
    }
  };

  const startOAuth = async () => {
    try {
      const response = await axios.get(`${API}/ewelink/auth-url`);
      setAuthUrl(response.data.auth_url);
      // Open in new window or redirect
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error("Errore generazione URL autenticazione");
    }
  };

  const disconnectEwelink = async () => {
    // Just clear the status display, actual logout would need API call
    setStatus({ connected: false, auth_required: true });
    toast.info("Per disconnetterti completamente, rimuovi l'app da eWeLink");
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
        <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
        <span className="text-sm text-slate-600">Verifica connessione...</span>
      </div>
    );
  }

  if (status?.connected) {
    return (
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <Check className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-green-800">eWeLink Connesso</p>
              <p className="text-sm text-green-600">{status.user?.email || status.user?.nickname || "Account collegato"}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={checkStatus}
            className="border-green-300"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Aggiorna
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
            <Wifi className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-blue-800">Collega il tuo account eWeLink</p>
            <p className="text-sm text-blue-600">Accedi con le tue credenziali eWeLink per gestire i dispositivi Sonoff</p>
          </div>
        </div>
        <Button
          onClick={startOAuth}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          <Zap className="h-4 w-4 mr-2" />
          Accedi con eWeLink
        </Button>
        <p className="text-xs text-blue-500 text-center">
          Verrai reindirizzato alla pagina di login eWeLink
        </p>
      </div>
    </div>
  );
};

// Centri Assistenza Manager Component
const CentriAssistenzaManager = () => {
  const [centri, setCentri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCentro, setEditingCentro] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    nome_azienda: "",
    referente: "",
    telefono: "",
    email: "",
    indirizzo: "",
    specializzazioni: [],
    note: ""
  });
  const [specInput, setSpecInput] = useState("");

  const loadCentri = async () => {
    try {
      const response = await axios.get(`${API}/centri-assistenza`);
      setCentri(response.data);
    } catch (error) {
      console.error("Error loading centri:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCentri();
  }, []);

  const handleOpenDialog = (centro = null) => {
    if (centro) {
      setEditingCentro(centro);
      setFormData({
        nome_azienda: centro.nome_azienda || "",
        referente: centro.referente || "",
        telefono: centro.telefono || "",
        email: centro.email || "",
        indirizzo: centro.indirizzo || "",
        specializzazioni: centro.specializzazioni || [],
        note: centro.note || ""
      });
    } else {
      setEditingCentro(null);
      setFormData({
        nome_azienda: "",
        referente: "",
        telefono: "",
        email: "",
        indirizzo: "",
        specializzazioni: [],
        note: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome_azienda || !formData.telefono) {
      toast.error("Nome azienda e telefono sono obbligatori");
      return;
    }

    try {
      if (editingCentro) {
        await axios.put(`${API}/centri-assistenza/${editingCentro.id}`, formData);
        toast.success("Centro assistenza aggiornato");
      } else {
        await axios.post(`${API}/centri-assistenza`, formData);
        toast.success("Centro assistenza creato");
      }
      setDialogOpen(false);
      loadCentri();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo centro assistenza?")) return;
    try {
      await axios.delete(`${API}/centri-assistenza/${id}`);
      toast.success("Centro eliminato");
      loadCentri();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const addSpecializzazione = () => {
    if (specInput.trim() && !formData.specializzazioni.includes(specInput.trim())) {
      setFormData({
        ...formData,
        specializzazioni: [...formData.specializzazioni, specInput.trim()]
      });
      setSpecInput("");
    }
  };

  const removeSpecializzazione = (spec) => {
    setFormData({
      ...formData,
      specializzazioni: formData.specializzazioni.filter(s => s !== spec)
    });
  };

  const filteredCentri = centri.filter(c => 
    !searchTerm ||
    c.nome_azienda?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.specializzazioni?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-500" />
            Centri Assistenza
          </CardTitle>
          <CardDescription>
            Gestisci i centri di assistenza per i tuoi elettrodomestici
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca centri..."
                className="pl-10"
              />
            </div>
            <Button onClick={() => handleOpenDialog()} data-testid="add-centro-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Centro
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredCentri.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? "Nessun centro trovato" : "Nessun centro assistenza configurato"}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCentri.map((centro) => (
                <div
                  key={centro.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <h4 className="font-medium">{centro.nome_azienda}</h4>
                      {centro.referente && (
                        <p className="text-sm text-gray-600">Ref: {centro.referente}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {centro.telefono}
                        </span>
                        {centro.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {centro.email}
                          </span>
                        )}
                      </div>
                      {centro.specializzazioni?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {centro.specializzazioni.map((spec, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(centro)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(centro.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Form */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCentro ? "Modifica Centro" : "Nuovo Centro Assistenza"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Azienda *</Label>
              <Input
                value={formData.nome_azienda}
                onChange={(e) => setFormData({ ...formData, nome_azienda: e.target.value })}
                placeholder="Es: Assistenza Samsung Sardegna"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Referente</Label>
                <Input
                  value={formData.referente}
                  onChange={(e) => setFormData({ ...formData, referente: e.target.value })}
                  placeholder="Nome referente"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefono *</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="0784 123456"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="assistenza@esempio.it"
              />
            </div>
            <div className="space-y-2">
              <Label>Indirizzo</Label>
              <Input
                value={formData.indirizzo}
                onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                placeholder="Via Roma 123, Nuoro"
              />
            </div>
            <div className="space-y-2">
              <Label>Specializzazioni</Label>
              <div className="flex gap-2">
                <Input
                  value={specInput}
                  onChange={(e) => setSpecInput(e.target.value)}
                  placeholder="Es: Samsung, Climatizzazione"
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSpecializzazione())}
                />
                <Button type="button" variant="outline" onClick={addSpecializzazione}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.specializzazioni.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.specializzazioni.map((spec, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {spec}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeSpecializzazione(spec)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                rows={2}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave}>
              {editingCentro ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Module configuration component
const ModulesConfig = ({ currentUser, setCurrentUser, authToken }) => {
  const TOGGLEABLE_MODULES = [
    { id: "matterport", label: "Vista 3D", icon: Eye, description: "Gemello digitale 3D (Matterport / MPSKIN)" },
    { id: "panorami360", label: "Panorami 360°", icon: Globe, description: "Panorami equirettangolari (upload + generazione AI)" },
    { id: "videocam", label: "Video Cam", icon: Camera, description: "Videosorveglianza" },
    { id: "sensori", label: "Report Sensori", icon: Activity, description: "Report e grafici sensori" },
    { id: "manutenzioni", label: "Manutenzioni", icon: Wrench, description: "Gestione manutenzioni e interventi" },
    { id: "calendario", label: "Calendario", icon: Calendar, description: "Calendario eventi e scadenze" },
    { id: "elettrodomestici", label: "Apparati", icon: Zap, description: "Gestione apparati ed elettrodomestici" },
    { id: "tickets", label: "Ticket", icon: Ticket, description: "Sistema di ticketing e segnalazioni" },
    { id: "veicoli", label: "Veicoli", icon: Car, description: "Tracciamento GPS veicoli (Balin)" },
    { id: "assistente", label: "Assistente AI", icon: Bot, description: "Assistente intelligente" },
    { id: "inventario", label: "Inventario", icon: Package, description: "Inventario ambienti e oggetti con AI" },
    { id: "dipendenti", label: "Operatori Manutenzioni", icon: Users, description: "Anagrafica operatori, assunzioni, stipendi e pagamenti" },
    { id: "design", label: "AI Design Assistant", icon: Wrench, description: "Render fotorealistici, template commerciali, inventario progetto" },
    { id: "computo", label: "Computo Metrico", icon: FileText, description: "Catalogo voci di lavoro e stime costi lavori" },
  ];

  const ALWAYS_ACTIVE = [
    { id: "smartdomo", label: "SmartDomo", icon: Thermometer, description: "Dashboard dispositivi smart" },
    { id: "proprieta", label: "Setup", icon: Settings, description: "Configurazione sistema" },
  ];

  const [modules, setModules] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const m = currentUser?.modules_enabled || {};
    const defaults = {};
    TOGGLEABLE_MODULES.forEach(mod => { defaults[mod.id] = m[mod.id] !== undefined ? m[mod.id] : true; });
    setModules(defaults);
  }, [currentUser]);

  const handleToggle = async (moduleId, enabled) => {
    const updated = { ...modules, [moduleId]: enabled };
    setModules(updated);
    setSaving(true);
    try {
      await axios.put(`${API}/users/${currentUser.id}/modules?token=${authToken}`, { modules_enabled: updated });
      toast.success(`Modulo ${enabled ? "attivato" : "disattivato"}`);
      // Update parent state with NEW object reference so React re-renders App.js
      // (top nav, isModuleEnabled, auto-redirect useEffect)
      if (typeof setCurrentUser === "function") {
        setCurrentUser(prev => prev ? { ...prev, modules_enabled: updated } : prev);
      }
    } catch (e) {
      toast.error("Errore salvataggio moduli");
      setModules(prev => ({ ...prev, [moduleId]: !enabled }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Always Active */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Moduli Sempre Attivi</CardTitle>
          <CardDescription>Questi moduli sono sempre visibili e non possono essere disattivati</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ALWAYS_ACTIVE.map(mod => (
              <div key={mod.id} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <mod.icon className="h-5 w-5 text-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-800">{mod.label}</p>
                  <p className="text-xs text-green-600">{mod.description}</p>
                </div>
                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">Attivo</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Toggleable Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Moduli Configurabili</CardTitle>
          <CardDescription>Attiva o disattiva i moduli in base alle tue esigenze</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TOGGLEABLE_MODULES.map(mod => {
              const enabled = modules[mod.id] !== false;
              return (
                <div key={mod.id} className={`flex items-center gap-3 p-3 border rounded-lg transition-all ${
                  enabled ? "bg-white border-gray-200" : "bg-gray-50 border-gray-200 opacity-60"
                }`}>
                  <mod.icon className={`h-5 w-5 shrink-0 ${enabled ? "text-blue-600" : "text-gray-400"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${enabled ? "text-gray-900" : "text-gray-500"}`}>{mod.label}</p>
                    <p className="text-xs text-gray-500">{mod.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input type="checkbox" checked={enabled} onChange={(e) => handleToggle(mod.id, e.target.checked)}
                      className="sr-only peer" disabled={saving} data-testid={`toggle-${mod.id}`} />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function PropertyConfig({ currentUser, setCurrentUser, authToken }) {
  const { t } = useLanguage();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smartThingsStatus, setSmartThingsStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    cadastral: {
      address: "",
      comune: "",
      provincia: "",
      cap: "",
      foglio: "",
      particella: "",
      subalterno: "",
      categoria: "",
      rendita: null,
      superficie_mq: null,
      vani: null,
      classe_energetica: "",
      anno_costruzione: null,
      note: ""
    },
    matterport: {
      space_id: "",
      sdk_key: "",
      enabled: true,
      mpskin_url: ""  // URL per tour MPSKIN alternativo
    },
    integrations: {
      smartthings: {
        enabled: false,
        token: "",
        location_id: "auto"
      },
      ewelink: {
        enabled: false,
        email: "",
        password: "",
        region: "eu"
      },
      balin: {
        enabled: false,
        email: "",
        api_token: ""
      }
    },
    ezviz: {
      enabled: false,
      username: "",
      password: "",
      app_key: "",
      secret: "",
      region: "eu"
    },
    weather: {
      enabled: true,
      city: "",
      lat: null,
      lon: null
    }
  });

  // Load property
  const loadProperty = useCallback(async () => {
    setLoading(true);
    try {
      // Try to get active property for current user
      let response = await axios.get(`${API}/property/active`, {
        params: { token: authToken }
      });
      
      if (!response.data) {
        // Initialize from env if no property exists for this user
        response = await axios.post(`${API}/property/init-from-env`, null, {
          params: { token: authToken }
        });
        if (response.data.property) {
          setProperty(response.data.property);
          setFormData(response.data.property);
          toast.success("Proprietà inizializzata dalla configurazione esistente");
        }
      } else {
        setProperty(response.data);
        // Merge property data with user-specific data
        setFormData(prevFormData => ({
          ...prevFormData,
          ...response.data,
          matterport: {
            ...prevFormData.matterport,
            ...response.data.matterport
          }
        }));
      }
    } catch (error) {
      console.error("Error loading property:", error);
      // If 404, try to init
      if (error.response?.status === 404 || !error.response) {
        try {
          const initResponse = await axios.post(`${API}/property/init-from-env`, null, {
            params: { token: authToken }
          });
          if (initResponse.data.property) {
            setProperty(initResponse.data.property);
            setFormData(prevFormData => ({
              ...prevFormData,
              ...initResponse.data.property,
              matterport: {
                ...prevFormData.matterport,
                ...initResponse.data.property.matterport
              }
            }));
          }
        } catch (initError) {
          toast.error("Errore nel caricamento della proprietà");
        }
      }
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Pre-populate MPSKIN URL and Space ID from currentUser if available
  // This runs after property is loaded to ensure user data takes precedence
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        matterport: {
          ...prev.matterport,
          // User's MPSKIN URL takes precedence
          mpskin_url: currentUser.mpskin_url || prev.matterport?.mpskin_url || "",
          // User's Matterport space_id takes precedence
          space_id: currentUser.matterport_space_id || prev.matterport?.space_id || ""
        }
      }));
    }
  }, [currentUser]);

  // Check SmartThings connection
  const checkSmartThingsConnection = useCallback(async () => {
    if (!formData.integrations?.smartthings?.enabled) {
      setSmartThingsStatus(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/smartthings/status`, { params: { token: authToken } });
      setSmartThingsStatus({
        connected: response.data.connected,
        deviceCount: response.data.device_count || 0,
        message: response.data.message
      });
    } catch (error) {
      setSmartThingsStatus({
        connected: false,
        error: error.response?.status === 401 ? "Token non valido" : "Errore connessione"
      });
    }
  }, [formData.integrations?.smartthings?.enabled, authToken]);

  useEffect(() => {
    loadProperty();
  }, [loadProperty]);

  useEffect(() => {
    if (!loading) {
      checkSmartThingsConnection();
    }
  }, [loading, checkSmartThingsConnection]);

  // Save property
  const saveProperty = async () => {
    if (!property?.id) {
      toast.error("Nessuna proprietà da salvare");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/property/${property.id}?token=${authToken}`, formData);
      
      // Also update user's MPSKIN URL and Matterport space if changed
      if (currentUser && authToken) {
        const userUpdate = {};
        if (formData.matterport?.mpskin_url !== currentUser.mpskin_url) {
          userUpdate.mpskin_url = formData.matterport?.mpskin_url || null;
        }
        if (formData.matterport?.space_id !== currentUser.matterport_space_id) {
          userUpdate.matterport_space_id = formData.matterport?.space_id || null;
        }
        
        if (Object.keys(userUpdate).length > 0) {
          await axios.put(`${API}/users/${currentUser.id}?token=${authToken}`, userUpdate);
          console.log("User MPSKIN/Matterport config updated:", userUpdate);
        }
      }
      
      toast.success("Configurazione salvata con successo!");
      await loadProperty();
      checkSmartThingsConnection();
    } catch (error) {
      console.error("Error saving property:", error);
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Update nested form data
  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split(".");
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Caricamento configurazione...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="property-config">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-600" />
            {t.setup.title}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gestisci i dati dell'immobile e le integrazioni smart home
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadProperty} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Ricarica
          </Button>
          <Button onClick={saveProperty} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t.setup.saveChanges}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            Generale
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2">
            <LayoutGrid className="h-4 w-4" />
            Moduli
          </TabsTrigger>
          <TabsTrigger value="cadastral" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Catastali
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Wifi className="h-4 w-4" />
            {t.setup.integrations}
          </TabsTrigger>
          <TabsTrigger value="matterport" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Matterport
          </TabsTrigger>
          <TabsTrigger value="centri" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Centri
          </TabsTrigger>
        </TabsList>


        {/* Tab: Moduli */}
        <TabsContent value="modules" className="space-y-4">
          <ModulesConfig currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken} />
        </TabsContent>

        {/* Tab: Generale */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informazioni Generali</CardTitle>
              <CardDescription>Nome e descrizione della proprietà</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Proprietà *</Label>
                  <Input
                    id="name"
                    value={formData.name || ""}
                    onChange={(e) => updateFormData("name", e.target.value)}
                    placeholder="Es: Villa Trivor"
                    data-testid="property-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descrizione</Label>
                  <Input
                    id="description"
                    value={formData.description || ""}
                    onChange={(e) => updateFormData("description", e.target.value)}
                    placeholder="Es: Residenza principale"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weather Config */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5 text-blue-500" />
                Configurazione Meteo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="weather-enabled"
                  checked={formData.weather?.enabled ?? true}
                  onCheckedChange={(checked) => updateFormData("weather.enabled", checked)}
                />
                <Label htmlFor="weather-enabled">Abilita widget meteo</Label>
              </div>
              
              {formData.weather?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weather-city">Città</Label>
                    <Input
                      id="weather-city"
                      value={formData.weather?.city || ""}
                      onChange={(e) => updateFormData("weather.city", e.target.value)}
                      placeholder="Es: Nuoro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weather-lat">Latitudine</Label>
                    <Input
                      id="weather-lat"
                      type="number"
                      step="0.0001"
                      value={formData.weather?.lat || ""}
                      onChange={(e) => updateFormData("weather.lat", parseFloat(e.target.value) || null)}
                      placeholder="Es: 40.3125"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weather-lon">Longitudine</Label>
                    <Input
                      id="weather-lon"
                      type="number"
                      step="0.0001"
                      value={formData.weather?.lon || ""}
                      onChange={(e) => updateFormData("weather.lon", parseFloat(e.target.value) || null)}
                      placeholder="Es: 9.3125"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Dati Catastali */}
        <TabsContent value="cadastral" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                Indirizzo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cadastral-address">Indirizzo completo</Label>
                <Input
                  id="cadastral-address"
                  value={formData.cadastral?.address || ""}
                  onChange={(e) => updateFormData("cadastral.address", e.target.value)}
                  placeholder="Via Roma 1"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cadastral-comune">Comune</Label>
                  <Input
                    id="cadastral-comune"
                    value={formData.cadastral?.comune || ""}
                    onChange={(e) => updateFormData("cadastral.comune", e.target.value)}
                    placeholder="Nuoro"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cadastral-provincia">Provincia</Label>
                  <Input
                    id="cadastral-provincia"
                    value={formData.cadastral?.provincia || ""}
                    onChange={(e) => updateFormData("cadastral.provincia", e.target.value)}
                    placeholder="NU"
                    maxLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cadastral-cap">CAP</Label>
                  <Input
                    id="cadastral-cap"
                    value={formData.cadastral?.cap || ""}
                    onChange={(e) => updateFormData("cadastral.cap", e.target.value)}
                    placeholder="08100"
                    maxLength={5}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-600" />
                Riferimenti Catastali
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cadastral-foglio">Foglio</Label>
                  <Input
                    id="cadastral-foglio"
                    value={formData.cadastral?.foglio || ""}
                    onChange={(e) => updateFormData("cadastral.foglio", e.target.value)}
                    placeholder="12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cadastral-particella">Particella</Label>
                  <Input
                    id="cadastral-particella"
                    value={formData.cadastral?.particella || ""}
                    onChange={(e) => updateFormData("cadastral.particella", e.target.value)}
                    placeholder="345"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cadastral-subalterno">Subalterno</Label>
                  <Input
                    id="cadastral-subalterno"
                    value={formData.cadastral?.subalterno || ""}
                    onChange={(e) => updateFormData("cadastral.subalterno", e.target.value)}
                    placeholder="1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cadastral-categoria">Categoria Catastale</Label>
                  <Select
                    value={formData.cadastral?.categoria || ""}
                    onValueChange={(value) => updateFormData("cadastral.categoria", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIE_CATASTALI.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cadastral-rendita">Rendita Catastale (€)</Label>
                  <Input
                    id="cadastral-rendita"
                    type="number"
                    step="0.01"
                    value={formData.cadastral?.rendita || ""}
                    onChange={(e) => updateFormData("cadastral.rendita", parseFloat(e.target.value) || null)}
                    placeholder="1250.00"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-5 w-5 text-purple-600" />
                Caratteristiche Immobile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cadastral-superficie">Superficie (mq)</Label>
                  <Input
                    id="cadastral-superficie"
                    type="number"
                    value={formData.cadastral?.superficie_mq || ""}
                    onChange={(e) => updateFormData("cadastral.superficie_mq", parseFloat(e.target.value) || null)}
                    placeholder="250"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cadastral-vani">Vani</Label>
                  <Input
                    id="cadastral-vani"
                    type="number"
                    value={formData.cadastral?.vani || ""}
                    onChange={(e) => updateFormData("cadastral.vani", parseInt(e.target.value) || null)}
                    placeholder="8"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cadastral-anno">Anno Costruzione</Label>
                  <Input
                    id="cadastral-anno"
                    type="number"
                    value={formData.cadastral?.anno_costruzione || ""}
                    onChange={(e) => updateFormData("cadastral.anno_costruzione", parseInt(e.target.value) || null)}
                    placeholder="1985"
                    min="1800"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Classe Energetica</Label>
                  <Select
                    value={formData.cadastral?.classe_energetica || ""}
                    onValueChange={(value) => updateFormData("cadastral.classe_energetica", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      {CLASSI_ENERGETICHE.map((cls) => (
                        <SelectItem key={cls.value} value={cls.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${cls.color}`} />
                            {cls.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cadastral-note">Note</Label>
                <Textarea
                  id="cadastral-note"
                  value={formData.cadastral?.note || ""}
                  onChange={(e) => updateFormData("cadastral.note", e.target.value)}
                  placeholder="Note aggiuntive sull'immobile..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Integrazioni */}
        <TabsContent value="integrations" className="space-y-4">
          {/* SmartThings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-green-600" />
                  <CardTitle>SmartThings</CardTitle>
                </div>
                <IntegrationStatus
                  enabled={formData.integrations?.smartthings?.enabled}
                  connected={smartThingsStatus?.connected}
                />
              </div>
              <CardDescription>
                Connetti i tuoi dispositivi Samsung SmartThings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="smartthings-enabled"
                  checked={formData.integrations?.smartthings?.enabled ?? false}
                  onCheckedChange={(checked) => updateFormData("integrations.smartthings.enabled", checked)}
                />
                <Label htmlFor="smartthings-enabled">Abilita SmartThings</Label>
              </div>
              
              {formData.integrations?.smartthings?.enabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="smartthings-token">Personal Access Token (PAT)</Label>
                    <PasswordInput
                      id="smartthings-token"
                      value={formData.integrations?.smartthings?.token}
                      onChange={(e) => updateFormData("integrations.smartthings.token", e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    />
                    <p className="text-xs text-gray-500">
                      Genera un token su{" "}
                      <a
                        href="https://account.smartthings.com/tokens"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        account.smartthings.com/tokens
                      </a>
                    </p>
                  </div>
                  
                  {smartThingsStatus && (
                    <div className={`p-3 rounded-lg ${smartThingsStatus.connected ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      {smartThingsStatus.connected ? (
                        <p className="text-green-700 text-sm">
                          ✅ Connesso! {smartThingsStatus.deviceCount} dispositivi trovati
                        </p>
                      ) : (
                        <p className="text-red-700 text-sm">
                          ❌ {smartThingsStatus.error}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* eWeLink */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wifi className="h-5 w-5 text-blue-600" />
                  <CardTitle>eWeLink / Sonoff</CardTitle>
                </div>
                <EweLinkStatus authToken={authToken} />
              </div>
              <CardDescription>
                Connetti dispositivi Sonoff e compatibili eWeLink tramite OAuth2
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="ewelink-enabled"
                  checked={formData.integrations?.ewelink?.enabled ?? false}
                  onCheckedChange={(checked) => updateFormData("integrations.ewelink.enabled", checked)}
                />
                <Label htmlFor="ewelink-enabled">Abilita eWeLink</Label>
              </div>
              
              {formData.integrations?.ewelink?.enabled && (
                <div className="space-y-4">
                  {/* OAuth2 Login Button */}
                  <EweLinkLoginSection />
                  
                  {/* Region selector */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ewelink-region">Regione</Label>
                      <Select
                        value={formData.integrations?.ewelink?.region || "eu"}
                        onValueChange={(value) => updateFormData("integrations.ewelink.region", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="eu">Europa</SelectItem>
                          <SelectItem value="us">USA</SelectItem>
                          <SelectItem value="cn">Cina</SelectItem>
                          <SelectItem value="as">Asia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ezviz */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-red-600" />
                  <CardTitle>Ezviz Telecamere</CardTitle>
                </div>
                <IntegrationStatus
                  enabled={formData.ezviz?.enabled}
                  connected={!!formData.ezviz?.username}
                />
              </div>
              <CardDescription>
                Integra le tue telecamere Ezviz / Hikvision
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="ezviz-enabled"
                  checked={formData.ezviz?.enabled ?? false}
                  onCheckedChange={(checked) => updateFormData("ezviz.enabled", checked)}
                />
                <Label htmlFor="ezviz-enabled">Abilita Ezviz</Label>
              </div>
              
              {formData.ezviz?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ezviz-username">Username/Email</Label>
                    <Input
                      id="ezviz-username"
                      value={formData.ezviz?.username || ""}
                      onChange={(e) => updateFormData("ezviz.username", e.target.value)}
                      placeholder="tua@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ezviz-password">Password</Label>
                    <PasswordInput
                      id="ezviz-password"
                      value={formData.ezviz?.password}
                      onChange={(e) => updateFormData("ezviz.password", e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ezviz-appkey">App Key</Label>
                    <PasswordInput
                      id="ezviz-appkey"
                      value={formData.ezviz?.app_key}
                      onChange={(e) => updateFormData("ezviz.app_key", e.target.value)}
                      placeholder="at.xxxxx"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ezviz-secret">Secret</Label>
                    <PasswordInput
                      id="ezviz-secret"
                      value={formData.ezviz?.secret}
                      onChange={(e) => updateFormData("ezviz.secret", e.target.value)}
                      placeholder="xxxxx"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Balin GPS */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                  <CardTitle>Balin GPS Tracker</CardTitle>
                </div>
                <BalinStatus />
              </div>
              <CardDescription>
                Traccia la posizione dei tuoi veicoli con localizzatori Balin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="balin-enabled"
                  checked={formData.integrations?.balin?.enabled ?? false}
                  onCheckedChange={(checked) => updateFormData("integrations.balin.enabled", checked)}
                />
                <Label htmlFor="balin-enabled">Abilita Balin GPS</Label>
              </div>
              
              {formData.integrations?.balin?.enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="balin-email">Email Account Balin</Label>
                      <Input
                        id="balin-email"
                        type="email"
                        value={formData.integrations?.balin?.email || ""}
                        onChange={(e) => updateFormData("integrations.balin.email", e.target.value)}
                        placeholder="tua@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="balin-token">API Token</Label>
                      <PasswordInput
                        id="balin-token"
                        value={formData.integrations?.balin?.api_token}
                        onChange={(e) => updateFormData("integrations.balin.api_token", e.target.value)}
                        placeholder="Il tuo API token Balin"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Ottieni l'API token dalle{" "}
                    <a
                      href="https://balin.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 hover:underline"
                    >
                      impostazioni del profilo su Balin.app
                    </a>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Matterport */}
        <TabsContent value="matterport" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-pink-600" />
                Configurazione Matterport
              </CardTitle>
              <CardDescription>
                Configura lo spazio 3D Matterport per il digital twin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="matterport-enabled"
                  checked={formData.matterport?.enabled ?? true}
                  onCheckedChange={(checked) => updateFormData("matterport.enabled", checked)}
                />
                <Label htmlFor="matterport-enabled">Abilita Vista 3D</Label>
              </div>
              
              {formData.matterport?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="matterport-spaceid">Space ID *</Label>
                    <Input
                      id="matterport-spaceid"
                      value={formData.matterport?.space_id || ""}
                      onChange={(e) => updateFormData("matterport.space_id", e.target.value)}
                      placeholder="j1r4zUjanif"
                    />
                    <p className="text-xs text-gray-500">
                      L'ID dello spazio Matterport (dalla URL: my.matterport.com/show/?m=<strong>ID</strong>)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="matterport-sdkkey">SDK Key</Label>
                    <PasswordInput
                      id="matterport-sdkkey"
                      value={formData.matterport?.sdk_key}
                      onChange={(e) => updateFormData("matterport.sdk_key", e.target.value)}
                      placeholder="xxxxxxxxxxxxx"
                    />
                    <p className="text-xs text-gray-500">
                      Ottieni la SDK Key dal{" "}
                      <a
                        href="https://matterport.com/developers"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Matterport Developer Portal
                      </a>
                    </p>
                  </div>
                  
                  {/* MPSKIN URL */}
                  <div className="pt-4 border-t">
                    <Label htmlFor="matterport-mpskin" className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-purple-600" />
                      MPSKIN Tour URL <span className="text-xs text-gray-500">(opzionale)</span>
                    </Label>
                    <Input
                      id="matterport-mpskin"
                      value={formData.matterport?.mpskin_url || ""}
                      onChange={(e) => updateFormData("matterport.mpskin_url", e.target.value)}
                      placeholder="https://tour.fairsgate.com/it/tour/xxx"
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Se configurato, verrà usato al posto del viewer Matterport nativo nella Dashboard.
                      La Vista 3D continuerà ad usare Matterport nativo per la gestione POI.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Matterport Cloud API - Per POI persistenti */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5 text-blue-600" />
                Matterport Cloud API
              </CardTitle>
              <CardDescription>
                Configura le credenziali API per sincronizzare i POI su my.matterport.com
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <p className="font-medium text-blue-800 mb-2">Come ottenere le credenziali:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Vai su <a href="https://my.matterport.com" target="_blank" rel="noopener noreferrer" className="underline">my.matterport.com</a></li>
                  <li>Settings (in basso a sinistra) → Developer Tools</li>
                  <li>Nella sezione <strong>API Token Management</strong>, clicca "Request API keys"</li>
                  <li>Copia il <strong>Token ID</strong> e il <strong>Token Secret</strong> (mostrato solo una volta!)</li>
                </ol>
                <p className="mt-2 text-blue-600 font-medium">
                  ⚠️ Il Token Secret viene mostrato solo una volta. Salvalo subito!
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="matterport-clientid">Token ID</Label>
                  <PasswordInput
                    id="matterport-clientid"
                    value={formData.matterport?.api_client_id}
                    onChange={(e) => updateFormData("matterport.api_client_id", e.target.value)}
                    placeholder="es: 90ec1bd71e4935b5"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="matterport-clientsecret">Token Secret</Label>
                  <PasswordInput
                    id="matterport-clientsecret"
                    value={formData.matterport?.api_client_secret}
                    onChange={(e) => updateFormData("matterport.api_client_secret", e.target.value)}
                    placeholder="es: c9c684136ae5..."
                  />
                </div>
              </div>

              {formData.matterport?.api_client_id && formData.matterport?.api_client_secret && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const response = await axios.post(`${API}/matterport/cloud/test-connection`, {
                          client_id: formData.matterport.api_client_id,
                          client_secret: formData.matterport.api_client_secret
                        });
                        if (response.data.connected) {
                          toast.success(response.data.message);
                        } else {
                          toast.error(response.data.error);
                        }
                      } catch (error) {
                        toast.error("Errore test connessione");
                      }
                    }}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Testa Connessione
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Centri Assistenza */}
        <TabsContent value="centri" className="space-y-4">
          <CentriAssistenzaManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
